import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import api from '../services/api';
import AnimatedNumber from '../components/AnimatedNumber';
import './Dashboard.css';

const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const formatMoney = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

const saludo = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
};

const KpiCard = ({ icon, tone, label, hint, children, delay = 0 }) => (
  <div className={`dx-kpi dx-tone-${tone}`} style={{ animationDelay: `${delay}ms` }}>
    <div className="dx-kpi-top">
      <span className="dx-kpi-icon">{icon}</span>
      <span className="dx-kpi-label">{label}</span>
    </div>
    <div className="dx-kpi-value">{children}</div>
    {hint && <div className="dx-kpi-hint">{hint}</div>}
  </div>
);

const Insight = ({ icon, tone, titulo, valor, detalle, progreso }) => (
  <div className={`dx-insight dx-tone-${tone}`}>
    <span className="dx-insight-icon">{icon}</span>
    <div className="dx-insight-body">
      <div className="dx-insight-row">
        <span className="dx-insight-title">{titulo}</span>
        <strong className="dx-insight-value">{valor}</strong>
      </div>
      {progreso != null && (
        <div className="dx-progress">
          <div className="dx-progress-fill" style={{ width: `${Math.max(0, Math.min(100, progreso))}%` }} />
        </div>
      )}
      <span className="dx-insight-detail">{detalle}</span>
    </div>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [ventas, setVentas] = useState([]);
  const [loadingVentas, setLoadingVentas] = useState(user?.tipoUsuario === 'FOTOGRAFO');
  const [kpisExtra, setKpisExtra] = useState(null);

  useEffect(() => {
    if (user?.tipoUsuario === 'FOTOGRAFO') {
      cargarVentas();
      cargarKpisExtra();
    }
  }, [user]);

  const cargarVentas = async () => {
    try {
      setLoadingVentas(true);
      const response = await api.get('/ventas/mis-ventas');
      setVentas(response.data);
    } catch (err) {
      // silencioso
    } finally {
      setLoadingVentas(false);
    }
  };

  const cargarKpisExtra = async () => {
    try {
      const response = await api.get('/dashboard/kpis');
      setKpisExtra(response.data);
    } catch (err) {
      // silencioso
    }
  };

  const calcularKPIs = () => {
    const completadas = ventas.filter(v => v.estado === 'COMPLETADA');
    const pendientes  = ventas.filter(v => v.estado === 'PENDIENTE');
    const ahora = new Date();
    const esteMes = completadas.filter(v => {
      if (!v.fechaVenta) return false;
      const f = new Date(v.fechaVenta);
      return f.getMonth() === ahora.getMonth() && f.getFullYear() === ahora.getFullYear();
    });

    return {
      totalVentas:      ventas.length,
      completadas:      completadas.length,
      pendientes:       pendientes.length,
      totalRecaudado:   completadas.reduce((s, v) => s + (v.montoTotal || 0), 0),
      misGanancias:     completadas.reduce((s, v) => s + (v.gananciafotografo || 0), 0),
      ventasEsteMes:    esteMes.length,
      gananciasEsteMes: esteMes.reduce((s, v) => s + (v.gananciafotografo || 0), 0),
    };
  };

  const serieMensual = () => {
    const ahora = new Date();
    const meses = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
      meses.push({ clave: `${d.getFullYear()}-${d.getMonth()}`, etiqueta: MESES_CORTOS[d.getMonth()], total: 0 });
    }
    ventas
      .filter(v => v.estado === 'COMPLETADA' && v.fechaVenta)
      .forEach(v => {
        const f = new Date(v.fechaVenta);
        const mes = meses.find(m => m.clave === `${f.getFullYear()}-${f.getMonth()}`);
        if (mes) mes.total += v.gananciafotografo || 0;
      });
    return meses;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const kpis = calcularKPIs();
  const esFotografo = user.tipoUsuario === 'FOTOGRAFO';
  const serie = serieMensual();
  const maxSerie = Math.max(...serie.map(m => m.total), 1);
  const totalSerie = serie.reduce((s, m) => s + m.total, 0);
  const primerNombre = (user.nombreCompleto || '').trim().split(/\s+/)[0];
  const ultimasVentas = [...ventas]
    .sort((a, b) => new Date(b.fechaVenta) - new Date(a.fechaVenta))
    .slice(0, 5);

  let mensajeHero = 'Bienvenido a Xaltech Fotos.';
  if (esFotografo) {
    if (loadingVentas) {
      mensajeHero = 'Preparando tu resumen...';
    } else if (kpis.gananciasEsteMes > 0) {
      mensajeHero = <>Este mes llevas <strong>{formatMoney(kpis.gananciasEsteMes)}</strong> en ganancias. ¡Sigue así!</>;
    } else {
      mensajeHero = 'Sube tus fotos y empieza a vender directo por WhatsApp.';
    }
  }

  return (
    <div className="dx-page">
      <section className="dx-hero">
        <div className="dx-hero-text">
          <span className="dx-hero-eyebrow">{saludo()}</span>
          <h1>Hola, {primerNombre} 👋</h1>
          <p>{mensajeHero}</p>
        </div>
        <div className="dx-hero-actions">
          {esFotografo && (
            <>
              <Link to="/eventos" state={{ crear: true }} className="dx-btn dx-btn-light">＋ Nuevo evento</Link>
              <Link to="/ventas" className="dx-btn dx-btn-outline">Ver mis ventas</Link>
            </>
          )}
          {user.tipoUsuario === 'ADMIN' && (
            <>
              <Link to="/eventos" className="dx-btn dx-btn-light">Ver todos los eventos</Link>
              <Link to="/ventas" className="dx-btn dx-btn-outline">Ver todas las ventas</Link>
            </>
          )}
          {user.tipoUsuario === 'ATLETA' && (
            <>
              <Link to="/buscar" className="dx-btn dx-btn-light">Buscar mis fotos</Link>
              <Link to="/ventas" className="dx-btn dx-btn-outline">Mis compras</Link>
            </>
          )}
        </div>
      </section>

      {esFotografo && (
        <>
          <div className="dx-section-title"><h2>Resumen de tu negocio</h2></div>

          {loadingVentas ? (
            <div className="dx-skeleton-grid">
              {[0, 1, 2, 3].map(i => <div key={i} className="dx-skeleton" />)}
            </div>
          ) : (
            <>
              <div className="dx-grid-main">
                <div className="dx-kpis">
                  <KpiCard icon="💰" tone="green" label="Ganancias totales" delay={0}
                    hint={`${formatMoney(kpis.totalRecaudado)} recaudados en total`}>
                    <AnimatedNumber value={kpis.misGanancias} format={formatMoney} />
                  </KpiCard>
                  <KpiCard icon="📅" tone="blue" label="Ganancias este mes" delay={80}
                    hint={`${kpis.ventasEsteMes} ${kpis.ventasEsteMes === 1 ? 'venta' : 'ventas'} este mes`}>
                    <AnimatedNumber value={kpis.gananciasEsteMes} format={formatMoney} />
                  </KpiCard>
                  <KpiCard icon="📸" tone="purple" label="Fotos vendidas" delay={160} hint="Ventas completadas">
                    <AnimatedNumber value={kpis.completadas} />
                  </KpiCard>
                  <KpiCard icon="⏳" tone="amber" label="Pagos pendientes" delay={240}
                    hint={kpis.pendientes > 0 ? 'Por confirmar' : 'Todo al día'}>
                    <AnimatedNumber value={kpis.pendientes} />
                  </KpiCard>
                </div>

                <div className="dx-chart-card">
                  <div className="dx-chart-head">
                    <div>
                      <h3>Ganancias recientes</h3>
                      <span>Últimos 6 meses</span>
                    </div>
                    <strong><AnimatedNumber value={totalSerie} format={formatMoney} /></strong>
                  </div>
                  <div className="dx-bars">
                    {serie.map((m, i) => (
                      <div key={m.clave} className="dx-bar-col" title={`${m.etiqueta}: ${formatMoney(m.total)}`}>
                        <div className="dx-bar-track">
                          <div
                            className={`dx-bar${i === serie.length - 1 ? ' dx-bar-current' : ''}`}
                            style={{ height: `${Math.max(4, (m.total / maxSerie) * 100)}%`, animationDelay: `${i * 90}ms` }}
                          />
                        </div>
                        <span>{m.etiqueta}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {kpisExtra && (
                <>
                  <div className="dx-section-title"><h2>Indicadores de tus eventos</h2></div>
                  <div className="dx-insights">
                    <Insight
                      icon="⏳" tone="purple" titulo="Atletas en lista de espera"
                      valor={<AnimatedNumber value={kpisExtra.atletasEnEspera} />}
                      detalle="Buscaron su foto y no la encontraron"
                    />
                    <Insight
                      icon="📊" tone="rose" titulo="Tasa de conversión"
                      valor={<AnimatedNumber value={Number(kpisExtra.tasaConversion) || 0} format={(n) => `${n.toFixed(1)}%`} />}
                      progreso={Number(kpisExtra.tasaConversion) || 0}
                      detalle="Fotos vendidas / total subidas"
                    />
                    <Insight
                      icon={kpisExtra.fotosSinDorsal > 0 ? '⚠️' : '✅'}
                      tone={kpisExtra.fotosSinDorsal > 0 ? 'amber' : 'green'}
                      titulo="Fotos sin dorsal detectado"
                      valor={<AnimatedNumber value={kpisExtra.fotosSinDorsal} />}
                      detalle={kpisExtra.fotosSinDorsal > 0 ? 'El OCR no pudo leer el número' : 'Todas tus fotos fueron reconocidas'}
                    />
                    {kpisExtra.tierWhatsapp && (
                      <Insight
                        icon="📱" tone="green" titulo="Cupo de WhatsApp hoy"
                        valor={<><AnimatedNumber value={kpisExtra.tierWhatsapp.cupoDisponible} /> / {kpisExtra.tierWhatsapp.limiteDiario}</>}
                        progreso={kpisExtra.tierWhatsapp.limiteDiario > 0
                          ? (kpisExtra.tierWhatsapp.cupoDisponible / kpisExtra.tierWhatsapp.limiteDiario) * 100
                          : 0}
                        detalle={`${kpisExtra.tierWhatsapp.usadas24h} conversaciones usadas en las últimas 24h`}
                      />
                    )}
                  </div>
                </>
              )}

              {ultimasVentas.length > 0 && (
                <>
                  <div className="dx-section-title">
                    <h2>Últimas ventas</h2>
                    <Link to="/ventas" className="dx-link">Ver todas →</Link>
                  </div>
                  <div className="dx-table-card">
                    <table className="dx-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Corredor</th>
                          <th>Monto</th>
                          <th>Mi ganancia</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ultimasVentas.map(v => (
                          <tr key={v.id}>
                            <td>{formatFecha(v.fechaVenta)}</td>
                            <td>{v.foto?.numeroCorredorDetectado || '-'}</td>
                            <td>{formatMoney(v.montoTotal)}</td>
                            <td className="dx-money">{formatMoney(v.gananciafotografo)}</td>
                            <td>
                              <span className={`dx-badge dx-badge-${v.estado?.toLowerCase()}`}>{v.estado}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {ventas.length === 0 && (
                <div className="dx-empty">
                  <span className="dx-empty-icon">📷</span>
                  <h3>¿Listo para tu primera venta?</h3>
                  <p>Crea un evento, sube tus fotos y comparte el link o QR para que los atletas las encuentren por WhatsApp.</p>
                  <Link to="/eventos" state={{ crear: true }} className="dx-btn dx-btn-primary">Crear mi primer evento</Link>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
